<template>
  <v-container fluid class="no-margins">
    <v-data-table
      class="table_card_view pt-5"
      :headers="[
        { text: 'Namn', value: 'firstName' },
        { text: $t('labels.mobilephone'), value: 'mobilephone' },
        { text: $t('labels.email2'), value: 'email' },
        { text: $t('labels.date'), value: 'createdAt' },
        { text: $t('labels.status'), value: 'state.name' },
        { text: '', value: 'id', sortable: false },
      ]"
      :items="applications"
      :options.sync="options"
      :server-items-length="applications.length"
      :items-per-page="itemsPerPage"
      item-key="id"
      :loading="isLoading"
      :hide-default-footer="applications.length>24 ? false : true"
      :footer-props="{
        showFirstLastPage: false,
        firstIcon: 'mdi-arrow-collapse-left',
        lastIcon: 'mdi-arrow-collapse-right',
        'items-per-page-options': [25, 50],
      }"
      locale="sv"
    >
      <!-- header first name -->
      <template slot="item.firstName" slot-scope="props">
        <div>{{ props.item.firstName }} {{ props.item.lastName }}</div>
      </template>
      <!-- header mobilephone -->
      <template slot="item.mobilephone" slot-scope="props">
        <!-- `<th>mobilephone</th>` -->
        <div v-if="props.item.isProtected">
          {{ props.item.mobilephone }}
        </div>
        <div v-else>
          <a v-bind:href="'tel:' + props.item.mobilephone">{{ props.item.mobilephone }}</a>
        </div>
      </template>
      <!-- header email -->
      <template slot="item.email" slot-scope="props">
        <!-- `<th>email</th>` -->
        <div v-if="props.item.isProtected">
          {{ props.item.email }}
        </div>
        <div v-else>
          <a v-bind:href="'mailto:' + props.item.email">{{ props.item.email }}</a>
        </div>
      </template>
      <!-- header createdAt -->
      <template slot="item.createdAt" slot-scope="props">
        <!-- `<th>createdAt</th>` -->
        <div>{{ props.item.createdAt | date }}</div>
      </template>
      <!-- header stateId -->
      <template slot="item.state.name" slot-scope="props">
        <!-- `<th>stateId</th>` -->
        <div>
          <v-icon>{{ applicationIconByName(props.item.state.state) }}</v-icon>
          {{ getStateName(props.item.state) }}
        </div>
      </template>
      <!-- header action buttons -->
      <template slot="item.id" slot-scope="props" class="mx-4">
        <!-- `<th>action buttons</th>` -->
        <ActionButton type="action center" @click="showApplication(props.item.id)">{{
          $t("labels.show-application")
        }}</ActionButton>
      </template>
    </v-data-table>
    <!-- Used to scroll applications on mobile versions -->
    <!-- <EndlessScroll @fetchMore="listenScroll" v-if="$vuetify.breakpoint.smAndDown" /> -->
    <v-btn
        fixed
        fab
        bottom
        right        
        class="elevation"
        color="primary"
        :disabled="isImitatingUser()"
        @click="
          $nextTick(() => {
            $router.push({
                    name: 'CreateCompanyApplications',
                    params: { id: $route.params.id, setStep: 0 },
                  });
          })
        "
      >
        <v-icon>$sl-add</v-icon>
      </v-btn>
  </v-container>
</template>

<script>
import API from "@/services/api.js";
import { mapGetters, mapActions } from "vuex";
import find from "lodash/find";
import debounce from "lodash/debounce";
import uniq from "lodash/uniq";
import uniqBy from "lodash/uniqBy";
import concat from "lodash/concat";
import ActionButton from "@/components/Common/ActionButton";
import EndlessScroll from "@/components/Common/EndlessScroll";
import { Icons } from "@/components/mixins";
import { CompanyRoles } from "@/components/mixins";

// @group Company
// ReviewApplications component
export default {
  name: "ReviewApplications",
  mixins: [Icons, CompanyRoles],
  components: {
    ActionButton,
    EndlessScroll,
  },
  props: {
    items: {
      type: Array,
      default: [],
    },
  },

  data() {
    return {
      applications: [],
      options: {},
      showScroll: false,
      startAt: 0,
      sortBy: "",
      sortDesc: "",
      itemsPerPage: 25,
      gotAll: false,
      isLoading: false,
      appStatusIcon: "",
      showFilter: ["md", "lg", "xl"].includes(this.$vuetify.breakpoint.name),
    };
  },

  computed: {
    ...mapGetters('currentuser', ['currentUser', 'isImitatingUser']),
    ...mapGetters("basic", ["basicStates", "getStateNameById"]),
    //apps(){}
  },

  created() {
    this.init();
  },

  watch: {
    /**
     * @vuese
     * Used to watch currentUser and then call debouncedOnChange method
     */
    items: {
      handler() {
        this.init();
      },
      deep: true,
    },
    options: {
      handler() {
        this.gotAll = false;
        //this.$emit('fetch')
        const { sortBy, sortDesc, page, itemsPerPage } = this.options;
        this.sortBy = sortBy;
        this.sortDesc = sortDesc;
        this.itemsPerPage = itemsPerPage;
        this.startAt = (page - 1) * itemsPerPage;
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions("application", ["setSelectedApplication"]),
    find: find,
    init() {
      this.applications = uniqBy(this.items, "id");      
    },
    /**
     * @vuese
     * Used to show the application state in difference language
     * @arg The argument is the application state
     */
    getStateName(state) {
      switch (state.state) {
        case "confirmedByStudent":
          return this.$t("application.state-confirmedByStudent");
          break;
        case "confirmedAndApprovedByCompany":
          return this.$t("application.state-confirmedAndApprovedByCompany");
          break;
        case "draft":
          return this.$t("application.state-draft");
          break;
        case "confirmedByCompany":
          return this.$t("application.state-confirmedByCompany");
          break;
        case "approved":
          return this.$t("application.state-approved");
          break;
        case "ordered":
          return this.$t("application.state-ordered");
          break;
        case "closed":
          return this.$t("application.state-closed");
          break;
        case "basicEducationCertificateIssued":
          return this.$t("application.state-basicEducationCertificateIssued");
          break;
        case "basicEducationCertificateApproved":
          return this.$t("application.state-basicEducationCertificateApproved");
          break;
        
        default:
          return state.name;
      }
    },
    /**
     * @vuese
     * Used to show an application
     * @arg The argument is the application id
     */
    showApplication(id) {
      API.getApplication(id)
        .then((response) => {
          this.setSelectedApplication(response.data);
          this.$nextTick(() => {
            this.$router.push({
              name: "AdminApplicationStart",
              params: { id: this.$route.params.id, application_id: id },
            });
          });
        })
        .catch((error) => {
          console.log("There was an error getApplication:", error);
        });
    },
  },
};
</script>

<style lang="scss"></style>
